import type { StateCreator } from 'zustand';
import { isAxiosError } from 'axios';
import { client } from '../../services/api';
import { createBespokeTemplateBespokeTemplatesPost } from '../../services/api/sdk.gen';
import { composeVibecodeLlmBundle } from '../../components/oasis/bespoke/layout-studio/composeVibecodeLlmBundle';
import type { Question } from '../../components/oasis/types';
import type { VisitTypeId } from '../../services/api/models/VisitTypeId';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { BespokeTemplateResponse } from '../../services/api/types.gen';
import { formatSectionDropdownLabel } from '../../pages/formSchema/formatSectionDropdownLabel';
import type {
  FormTreeQuestion,
  SectionGenerationStatus,
} from '../../pages/formSchema/formSchemaTypes';
import {
  bespokeApiErrorMessage,
  layoutGenerationKey,
  requireSavedBespokeTemplate,
  validateLayoutJsonForSection,
} from '../../pages/formSchema/formSchemaUtils';
import type { VibecoderSettingsSlice } from './vibecoderSettingsSlice';

export type FormSchemaLayoutGenerationToast = {
  error: (title: string, description?: string) => void;
  success: (title: string, description?: string) => void;
};

export type StartFormSchemaLayoutGenerationArgs = {
  formType: RogerDocFormType;
  schemaVersionName: string;
  formSchemaVersion: string;
  sectionName: string;
  questions: FormTreeQuestion[];
  /** Trimmed prompt */
  prompt: string;
  visitTypeSummary: string;
  /** Roger Docs API visit_type for Patient Manager shell rules (`canonical_visit_type_id`). */
  canonicalVisitTypeId?: VisitTypeId | null;
  isOasis?: boolean;
  usedDefaultPrompt: boolean;
  /** Patch local state with the created row (avoids refetching the full list). */
  onTemplateSaved: (saved: BespokeTemplateResponse) => void;
  toast: FormSchemaLayoutGenerationToast;
};

export interface FormSchemaLayoutGenerationSlice {
  sectionGenerationStatusByKey: Record<string, SectionGenerationStatus>;
  startFormSchemaLayoutGeneration: (
    args: StartFormSchemaLayoutGenerationArgs
  ) => void;
}

export const formSchemaLayoutGenerationSlice: StateCreator<
  FormSchemaLayoutGenerationSlice & VibecoderSettingsSlice,
  [],
  [],
  FormSchemaLayoutGenerationSlice
> = (set, get) => ({
  sectionGenerationStatusByKey: {},

  startFormSchemaLayoutGeneration: (
    args: StartFormSchemaLayoutGenerationArgs
  ) => {
    const {
      formType,
      schemaVersionName,
      formSchemaVersion,
      sectionName,
      questions,
      prompt: generationPrompt,
      visitTypeSummary,
      canonicalVisitTypeId,
      isOasis,
      usedDefaultPrompt,
      onTemplateSaved,
      toast,
    } = args;

    if (!generationPrompt) {
      toast.error('Prompt required', 'Enter a prompt to generate a layout.');
      return;
    }

    const key = layoutGenerationKey(schemaVersionName, sectionName);
    const displayName = formatSectionDropdownLabel(sectionName);

    set(state => ({
      sectionGenerationStatusByKey: {
        ...state.sectionGenerationStatusByKey,
        [key]: { state: 'loading', activity: 'generating' },
      },
    }));

    void (async () => {
      try {
        const bundle = composeVibecodeLlmBundle({
          visitTypeSummary,
          schemaVersionName,
          sectionName,
          questions: questions as Question[],
          canonicalVisitTypeId,
          isOasis,
        });
        // Pull the user's last-picked model + temperature from the shared
        // store slice; falls back to the backend registry default when null.
        const { vibecoderModelId, vibecoderTemperature } = get();
        const res = await client.post({
          url: '/vibecoder-layout-assistant/chat',
          body: {
            messages: [
              {
                role: 'user',
                content: generationPrompt,
              },
            ],
            context_bundle: bundle,
            current_layout_json_text: '[]',
            require_full_layout: true,
            ...(vibecoderModelId ? { model: vibecoderModelId } : {}),
            ...(vibecoderTemperature != null &&
            !Number.isNaN(vibecoderTemperature)
              ? { temperature: vibecoderTemperature }
              : {}),
          },
          headers: {
            'Content-Type': 'application/json',
          },
          responseType: 'json',
          throwOnError: true,
        });
        const data = res.data as {
          assistant_message?: string;
          layout_json?: unknown[] | null;
        };
        if (!Array.isArray(data.layout_json)) {
          const hint = data.assistant_message?.trim()
            ? ` ${data.assistant_message.trim()}`.replace(/\s+/g, ' ')
            : '';
          throw new Error(
            `The assistant did not return a layout JSON array.${hint || ' The model may have asked for clarification (older API), or the response shape was unexpected.'}`
          );
        }

        const layoutJsonText = JSON.stringify(data.layout_json);
        const validation = validateLayoutJsonForSection(
          layoutJsonText,
          questions
        );
        if (!validation.ok) {
          throw new Error(validation.error);
        }

        const created = await createBespokeTemplateBespokeTemplatesPost({
          body: {
            form_type: formType,
            schema_version_name: schemaVersionName,
            form_schema_version: formSchemaVersion,
            section_name: sectionName,
            layout_json: validation.layout,
            name: `${displayName} LLM layout ${new Date().toISOString()}`,
            description: usedDefaultPrompt
              ? 'Generated from the Form Tree default LLM prompt.'
              : 'Generated from a custom Form Tree LLM prompt.',
            status: 'live',
          },
          throwOnError: true,
        });
        // Fail closed before reporting success: the row was created `live`, so
        // without it we can't patch local state and the list would keep the
        // section's prior active layout showing as current.
        const savedTemplate = requireSavedBespokeTemplate(created.data);

        set(state => ({
          sectionGenerationStatusByKey: {
            ...state.sectionGenerationStatusByKey,
            [key]: {
              state: 'success',
              message: 'Generated and enabled.',
            },
          },
        }));
        toast.success(
          'Layout Generated',
          `${displayName} now has an enabled custom layout.`
        );
        // Patch just the new row into local state (it was created `live`, so
        // the shared helper also demotes the section's prior active row) rather
        // than refetching the entire catalog and churning every row's identity.
        onTemplateSaved(savedTemplate);
      } catch (err) {
        const message = isAxiosError(err)
          ? bespokeApiErrorMessage(err)
          : err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.';
        console.error('Failed to generate section Roger Layout Schema:', err);
        set(state => ({
          sectionGenerationStatusByKey: {
            ...state.sectionGenerationStatusByKey,
            [key]: { state: 'error', message },
          },
        }));
        toast.error('Generation Failed', `${displayName}: ${message}`);
      }
    })();
  },
});
